/**
 * Health sync helper — Android Health Connect via our custom Kotlin plugin.
 *
 * iter34: @capgo/capacitor-health was removed entirely. It pinned the
 * Gradle build to JDK 21 (causing local Android Studio toolchain errors on
 * the tester's Windows machine) AND its requestAuthorization() silently
 * auto-denied on Android 14+ — bugs we ate three iterations to work around.
 *
 * The custom Kotlin plugin in `frontend/scripts/install-custom-health-plugin.js`
 * is the only path now. iOS will need its own implementation later (TODO).
 * On web (preview / dev) we return a friendly "not available" so the UI
 * gracefully hides step-sync controls.
 */
import { Capacitor } from "@capacitor/core";
import { api } from "./api";
import { hcnIsAvailable, hcnCheckPermissions, hcnRequestPermissions, hcnReadStepsToday, hcnOpenSettings } from "./healthNative";

export function isHealthSupported() {
  // Flag gate (set in frontend/.env). When 0, hide all step-sync UI.
  if (process.env.REACT_APP_ENABLE_HEALTH !== "1") return false;
  // Must be running natively (Android via Capacitor).
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

/**
 * Async, deeper check that also verifies the native Health plugin is wired.
 * Returns `{ supported: bool, reason?: string }` so the UI can show a helpful
 * "Health Connect not installed" / "Plugin missing" message instead of just
 * hiding the button silently.
 */
export async function probeHealthSupport() {
  if (process.env.REACT_APP_ENABLE_HEALTH !== "1") {
    return { supported: false, reason: "flag-disabled" };
  }
  if (!Capacitor.isNativePlatform()) {
    return { supported: false, reason: "web" };
  }
  if (Capacitor.getPlatform() !== "android") {
    return { supported: false, reason: "platform-unsupported" };
  }
  try {
    const avail = await hcnIsAvailable();
    if (avail.available) return { supported: true };
    if (avail.sdkStatus === 2) {
      return { supported: false, reason: "health-connect-needs-update" };
    }
    return { supported: false, reason: "health-connect-missing" };
  } catch (e) {
    return { supported: false, reason: "plugin-not-linked", detail: String(e?.message || e) };
  }
}

/**
 * Read today's step count and forward to the backend. Returns:
 *   { ok: true, steps, energy_granted, profile }
 *   { ok: false, reason: "no-permission" | "not-installed" | "plugin-not-linked" |
 *                        "web" | "timeout" | "error", detail?: string }
 */
export async function syncStepsToBackend() {
  if (!isHealthSupported()) {
    // eslint-disable-next-line no-console
    console.warn("[health] sync skipped — not native or flag off");
    return { ok: false, reason: "web" };
  }

  // 1. Check that Health Connect is available on this device.
  let avail;
  try {
    avail = await hcnIsAvailable();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[health] hcnIsAvailable threw:", e);
    return { ok: false, reason: "plugin-not-linked", detail: String(e?.message || e) };
  }
  if (!avail.available) {
    if (avail.sdkStatus === 2) {
      return { ok: false, reason: "not-installed", detail: "Health Connect needs update" };
    }
    return { ok: false, reason: "not-installed", detail: `sdkStatus=${avail.sdkStatus}` };
  }

  // 2. Permission gate — use the canonical getGrantedPermissions() check,
  // NOT a step read. Previously we read steps first and launched the
  // permission picker whenever that read failed, so ANY transient read
  // error (HC busy, no data yet, momentary SecurityException) re-popped
  // the "Allow Fitjong to access Health Connect" dialog. Tester report:
  // "I get the Health Connect permission prompt every day when I tap
  // Refresh." Now we only launch the picker when the permission is
  // genuinely not granted. When it IS granted, requestHealthPermissions
  // would resolve instantly with no UI anyway, so falling through to it
  // on an old build (unsupported check) is still safe.
  const perm = await hcnCheckPermissions();
  if (!perm.granted) {
    // THIS is the call that shows the Health Connect permission UI on
    // Android 14+ via PermissionController.createRequestPermissionResultContract().
    const reqRes = await hcnRequestPermissions();
    if (!reqRes.granted) {
      // eslint-disable-next-line no-console
      console.warn("[health-native] permission not granted:", reqRes);
      return { ok: false, reason: "no-permission", detail: JSON.stringify(reqRes) };
    }
  }

  // 3. Read today's steps. Permission is granted now, so a failure here is
  // a genuine read error — we do NOT re-prompt.
  const readRes = await hcnReadStepsToday();
  if (!readRes.ok) {
    return { ok: false, reason: "error", detail: readRes.error || "read failed" };
  }
  const steps = readRes.steps || 0;

  // 4. POST to backend — server awards energy idempotently.
  try {
    const { data } = await api.post("/health/sync-steps", {
      steps_today: steps,
      platform: "android",
      // Pass the user's TZ offset (minutes BEHIND UTC) so the backend can
      // bucket "today" against LOCAL midnight, not UTC midnight. Fixes the
      // "energy resets at UTC midnight, not my midnight" tester report.
      timezone_offset_minutes: new Date().getTimezoneOffset(),
    });
    return { ok: true, steps, energy_granted: data.energy_granted, profile: data.profile };
  } catch (e) {
    const errMsg = e?.response?.data?.detail || e?.message || String(e);
    return { ok: false, reason: "error", detail: errMsg };
  }
}

/**
 * Auto-sync hook — call from Dashboard mount + after game completion to
 * silently pull the latest step count into the profile. No UI prompts;
 * if permission isn't granted yet it just no-ops and waits for the user
 * to tap "Sync steps" manually. Throttled to once per 5 minutes to avoid
 * battery drain.
 */
const AUTO_SYNC_KEY = "fitjong_last_step_sync_v1";
const AUTO_SYNC_MIN_INTERVAL_MS = 5 * 60 * 1000;

export async function autoSyncStepsIfDue() {
  if (!isHealthSupported()) return { skipped: "web" };
  try {
    const last = Number(localStorage.getItem(AUTO_SYNC_KEY) || 0);
    if (Date.now() - last < AUTO_SYNC_MIN_INTERVAL_MS) {
      return { skipped: "throttled" };
    }
  } catch (_) {}
  // Skip auto-sync if we don't already have permission — we don't want to
  // pop the permission picker on every Dashboard mount. Check the grant
  // state directly (no UI, no read side-effects); if it's not granted we
  // wait for the user to tap "Sync steps" manually.
  const perm = await hcnCheckPermissions();
  if (!perm.granted) return { skipped: "no-permission" };

  const result = await syncStepsToBackend();
  if (result.ok) {
    try { localStorage.setItem(AUTO_SYNC_KEY, String(Date.now())); } catch (_) {}
  }
  return result;
}

export function lastSyncEpoch() {
  try { return Number(localStorage.getItem(AUTO_SYNC_KEY) || 0); } catch (_) { return 0; }
}

/**
 * Open the native Health Connect app directly to Fitjong's permission page.
 * Uses our custom Kotlin plugin's openSettings().
 */
export async function openHealthConnect() {
  if (!isHealthSupported()) return { ok: false, reason: "web" };
  try {
    return await hcnOpenSettings();
  } catch (e) {
    return { ok: false, reason: "error", detail: String(e?.message || e) };
  }
}
